import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import { formatDate, getNewsById, getRelatedNews } from '@/lib/catalog';
import { specials } from '@/mocks/specials';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const news = getNewsById(id);
  const [toast, setToast] = useState(false);

  useDocumentMeta({
    title: news ? `资讯《${news.title}》全文 - 午夜电影网` : '新闻不存在 - 午夜电影网',
    description: news ? news.excerpt : '这篇资讯可能已经被移除，您可以返回资讯列表继续浏览最新电影动态。',
    keywords: news ? `${news.title},${news.category},电影资讯,午夜电影网` : '电影资讯,午夜电影网',
  });

  if (!news) {
    return (
      <div className="shell pb-20 pt-32">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影资讯', to: '/news' }, { label: '新闻不存在' }]} />
        <div className="mt-8">
          <EmptyState
            icon="ri-newspaper-line"
            title="新闻不存在"
            description="这篇资讯可能已经被删除或链接有误。回到资讯列表，还有更多最新电影动态在等你。"
            actionLabel="返回资讯列表"
            actionTo="/news"
          />
        </div>
      </div>
    );
  }

  const related = getRelatedNews(news, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setToast(true);
      window.setTimeout(() => setToast(false), 2400);
    } catch {
      setToast(true);
      window.setTimeout(() => setToast(false), 2400);
    }
  };

  return (
    <article>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-8 pt-24 md:pb-10 md:pt-32">
        <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电影资讯', to: '/news' }, { label: news.category }]} />

          <div className="mt-5 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded bg-primary-500 px-2 py-0.5 font-medium text-background-50">{news.category}</span>
              <span className="text-foreground-500">{formatDate(news.date)}</span>
              <span className="h-1 w-1 rounded-full bg-foreground-500/60" />
              <span className="text-foreground-500">文 / {news.author}</span>
            </div>

            <h1 className="mt-4 font-heading text-3xl font-semibold leading-tight tracking-wide text-foreground-950 md:text-4xl lg:text-5xl">
              {news.title}
            </h1>
            <p className="mt-3 text-sm leading-7 text-foreground-500 md:text-base">{news.subtitle}</p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleCopy}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-500/70 hover:text-primary-500"
              >
                <i className="ri-link" aria-hidden="true"></i>
                复制文章链接
              </button>
              <Link to="/news" className="whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-500">
                ← 返回资讯列表
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="shell py-10 md:py-14">
        <div className="mx-auto max-w-4xl">
          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={news.cover}
              alt={news.title}
              title={`${news.title} - 资讯配图`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <p className="mt-2 text-[11px] text-foreground-500">配图：午夜电影网资料馆（演示图片）</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="space-y-5 text-sm leading-8 text-foreground-600 md:text-[15px] md:leading-9">
              {news.content.map((paragraph, index) => (
                <p key={`${news.id}-p-${index}`} className={index === 0 ? 'text-base text-foreground-800 md:text-lg md:leading-9' : ''}>
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border-l-2 border-primary-500 bg-background-100 px-5 py-4">
              <p className="text-xs leading-7 text-foreground-500">
                本文为午夜电影网原创演示内容，文中影片、人物与数据均为示例，不代表真实市场信息。转载请注明出处。
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-foreground-500">相关标签：</span>
              {[news.category, '电影观察', '午夜片单', '银幕之外'].map((tag) => (
                <span
                  key={tag}
                  className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <aside className="space-y-4 lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                  <i className="ri-book-read-line text-primary-500" aria-hidden="true"></i>
                  相关阅读
                </h2>
                <div className="mt-4 space-y-4">
                  {related.map((item) => (
                    <Link key={item.id} to={`/news/${item.id}`} className="group flex gap-3">
                      <span className="h-14 w-20 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 block text-xs leading-6 text-foreground-700 transition-colors group-hover:text-primary-500">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[10px] text-foreground-500">{formatDate(item.date)}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="flex items-center gap-2 font-heading text-xl tracking-wide text-foreground-950">
                  <i className="ri-film-line text-primary-500" aria-hidden="true"></i>
                  边看边读
                </h2>
                <div className="mt-4 space-y-3">
                  {specials.slice(0, 2).map((item) => (
                    <Link key={item.id} to={`/specials/${item.id}`} className="group block">
                      <span className="line-clamp-1 block text-xs text-foreground-700 transition-colors group-hover:text-primary-500">
                        {item.title}
                      </span>
                      <span className="mt-1 block text-[10px] text-foreground-500">{item.tag} · {item.movieIds.length} 部影片</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="animate-fade-in fixed inset-x-4 bottom-6 z-[70] mx-auto w-fit max-w-[92%] rounded-md border border-primary-500/40 bg-background-100 px-4 py-2.5 text-xs text-foreground-800">
          文章链接已复制
        </div>
      )}
    </article>
  );
}